/**
 * Mock upcoming portfolio events for dashboard modules.
 * Generates compliance, contractual, and operational deadlines
 * for the first 5 sites in a portfolio.
 */

export type UpcomingEventType =
  | 'nerc_report'
  | 'warranty_expiration'
  | 'om_expiration'
  | 'ppa_milestone'
  | 'insurance_renewal'
  | 'grid_interconnection_review'
  | 'environmental_permit'
  | 'inverter_replacement'
  | 'panel_cleaning'
  | 'tax_equity_flip';

export interface UpcomingPortfolioEvent {
  id: string;
  site_id: string;
  site_name: string;
  event_type: UpcomingEventType;
  title: string;
  description: string;
  due_date: string; // ISO 8601
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: 'compliance' | 'contractual' | 'operational' | 'financial';
}

export const UPCOMING_EVENT_TYPE_CONFIG: Record<UpcomingEventType, {
  label: string;
  category: UpcomingPortfolioEvent['category'];
  color: string;
  defaultSeverity: UpcomingPortfolioEvent['severity'];
}> = {
  nerc_report: { label: 'NERC Report', category: 'compliance', color: '#EF4444', defaultSeverity: 'critical' },
  warranty_expiration: { label: 'Warranty Expiration', category: 'contractual', color: '#F97316', defaultSeverity: 'high' },
  om_expiration: { label: 'O&M Expiration', category: 'contractual', color: '#F59E0B', defaultSeverity: 'high' },
  ppa_milestone: { label: 'PPA Milestone', category: 'financial', color: '#8B5CF6', defaultSeverity: 'medium' },
  insurance_renewal: { label: 'Insurance Renewal', category: 'financial', color: '#3B82F6', defaultSeverity: 'medium' },
  grid_interconnection_review: { label: 'Grid Interconnection Review', category: 'compliance', color: '#06B6D4', defaultSeverity: 'high' },
  environmental_permit: { label: 'Environmental Permit', category: 'compliance', color: '#22C55E', defaultSeverity: 'medium' },
  inverter_replacement: { label: 'Inverter Replacement', category: 'operational', color: '#EC4899', defaultSeverity: 'medium' },
  panel_cleaning: { label: 'Panel Cleaning', category: 'operational', color: '#84CC16', defaultSeverity: 'low' },
  tax_equity_flip: { label: 'Tax Equity Flip', category: 'financial', color: '#A855F7', defaultSeverity: 'critical' },
};

export const CATEGORY_COLORS: Record<UpcomingPortfolioEvent['category'], string> = {
  compliance: '#EF4444',
  contractual: '#F59E0B',
  operational: '#3B82F6',
  financial: '#8B5CF6',
};

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Generate mock upcoming events for given sites.
 * Dates range from next week to ~5 years from now.
 */
export function createMockUpcomingEvents(
  sites: Array<{ site_id?: string; id?: string; site_name?: string; name?: string }>,
  maxSites = 5,
): UpcomingPortfolioEvent[] {
  const now = new Date();
  const events: UpcomingPortfolioEvent[] = [];
  const useSites = sites.slice(0, maxSites);

  // Each site gets a varied set of upcoming events at different time horizons
  const templates: Array<{
    type: UpcomingEventType;
    titleFn: (siteName: string) => string;
    descFn: (siteName: string) => string;
    dayOffsets: number[]; // days from now — one per site
  }> = [
    {
      type: 'nerc_report',
      titleFn: (s) => `NERC CIP Compliance Report — ${s}`,
      descFn: (s) => `Annual NERC CIP compliance report due for ${s}. Includes cybersecurity controls audit and critical infrastructure protection documentation.`,
      dayOffsets: [14, 45, 90, 180, 365],
    },
    {
      type: 'warranty_expiration',
      titleFn: (s) => `Inverter Warranty Expiration — ${s}`,
      descFn: (s) => `SMA/Enphase inverter warranty expiring at ${s}. Review replacement or extended warranty options.`,
      dayOffsets: [30, 120, 365, 730, 1095],
    },
    {
      type: 'om_expiration',
      titleFn: (s) => `O&M Contract Expiration — ${s}`,
      descFn: (s) => `Operations & Maintenance agreement expiring for ${s}. Begin RFP process for renewal or replacement vendor.`,
      dayOffsets: [60, 200, 400, 800, 1460],
    },
    {
      type: 'ppa_milestone',
      titleFn: (s) => `PPA Rate Escalation — ${s}`,
      descFn: (s) => `Power Purchase Agreement rate escalation milestone for ${s}. Review contracted escalation terms.`,
      dayOffsets: [90, 365, 730, 1095, 1825],
    },
    {
      type: 'insurance_renewal',
      titleFn: (s) => `Property & Liability Insurance Renewal — ${s}`,
      descFn: (s) => `Annual insurance policy renewal for ${s}. Obtain competitive quotes and update coverage limits.`,
      dayOffsets: [21, 60, 180, 365, 540],
    },
    {
      type: 'grid_interconnection_review',
      titleFn: (s) => `Grid Interconnection Agreement Review — ${s}`,
      descFn: (s) => `Periodic grid interconnection agreement review with utility for ${s}. Confirm compliance with interconnection requirements.`,
      dayOffsets: [45, 150, 365, 730, 1095],
    },
    {
      type: 'environmental_permit',
      titleFn: (s) => `Environmental Permit Renewal — ${s}`,
      descFn: (s) => `Environmental compliance permit renewal for ${s}. Submit updated stormwater management and habitat assessments.`,
      dayOffsets: [120, 300, 545, 900, 1460],
    },
    {
      type: 'inverter_replacement',
      titleFn: (s) => `Scheduled Inverter Replacement — ${s}`,
      descFn: (s) => `Planned inverter replacement cycle for aging units at ${s}. Coordinate with O&M provider.`,
      dayOffsets: [180, 365, 730, 1095, 1825],
    },
    {
      type: 'panel_cleaning',
      titleFn: (s) => `Bi-Annual Panel Cleaning — ${s}`,
      descFn: (s) => `Scheduled panel cleaning service for ${s}. Coordinate with cleaning vendor.`,
      dayOffsets: [7, 14, 30, 60, 90],
    },
    {
      type: 'tax_equity_flip',
      titleFn: (s) => `Tax Equity Partnership Flip Date — ${s}`,
      descFn: (s) => `Tax equity partnership ownership flip date approaching for ${s}. Coordinate with legal and financial advisors.`,
      dayOffsets: [365, 545, 730, 1095, 1825],
    },
  ];

  templates.forEach((template) => {
    useSites.forEach((site, siteIdx) => {
      const siteId = site.site_id || site.id || `site-${siteIdx}`;
      const siteName = site.site_name || site.name || `Site ${siteIdx + 1}`;
      const dayOffset = template.dayOffsets[siteIdx % template.dayOffsets.length];
      const config = UPCOMING_EVENT_TYPE_CONFIG[template.type];

      // Add some jitter so dates aren't perfectly aligned
      const jitter = ((siteIdx * 7 + template.type.charCodeAt(0)) % 15) - 7;

      events.push({
        id: `upcoming-${template.type}-${siteId}`,
        site_id: siteId,
        site_name: siteName,
        event_type: template.type,
        title: template.titleFn(siteName),
        description: template.descFn(siteName),
        due_date: addDays(now, dayOffset + jitter).toISOString(),
        severity: config.defaultSeverity,
        category: config.category,
      });
    });
  });

  return events.sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());
}

/**
 * Returns events grouped by month for calendar display.
 */
export function groupEventsByMonth(events: UpcomingPortfolioEvent[]): Map<string, UpcomingPortfolioEvent[]> {
  const grouped = new Map<string, UpcomingPortfolioEvent[]>();
  events.forEach((event) => {
    const d = new Date(event.due_date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(event);
  });
  return grouped;
}

/**
 * Returns events grouped by date for heatmap display (GitHub-style).
 */
export function groupEventsByDate(events: UpcomingPortfolioEvent[]): Map<string, UpcomingPortfolioEvent[]> {
  const grouped = new Map<string, UpcomingPortfolioEvent[]>();
  events.forEach((event) => {
    const d = new Date(event.due_date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(event);
  });
  return grouped;
}
